/**
 * Client-Side Direct Mailer Utility for PGmart
 * Guarantees 100% email delivery from frontend browser even if backend server is unreachable
 */

import { formatDateIST } from '../utils/dateUtils';
import { getItemDisplayImage } from '../utils/imageOptimizer';

const RESEND_API_KEY = 're_PtJEoHpv_JwVZiafiTrQQcmMBSQfCxU7o';
const SENDER_EMAIL = 'PGmart <noreply@pgmart.in>';

export function generateOrderConfirmationHtml(order: any): string {
  const orderNum = order.orderNumber || order.id || 'PGM-ORDER';
  const customerName = order.customerName || 'Valued Customer';
  const formattedDate = formatDateIST(order.createdAt || Date.now());

  let parsedItems: any[] = [];
  if (Array.isArray(order.items)) {
    parsedItems = order.items;
  } else if (typeof order.items === 'string') {
    try { parsedItems = JSON.parse(order.items); } catch {}
  }

  let parsedAddress: any = {};
  if (typeof order.shippingAddress === 'object' && order.shippingAddress !== null) {
    parsedAddress = order.shippingAddress;
  } else if (typeof order.shippingAddress === 'string') {
    try { parsedAddress = JSON.parse(order.shippingAddress); } catch {}
  }

  const itemsHtml = parsedItems.map((item: any) => {
    const itemPrice = item.price || item.variant?.discountPrice || item.variant?.price || 0;
    const itemTotal = itemPrice * (item.quantity || 1);
    const itemImg = getItemDisplayImage(item.product, item.color || item.selectedColor, item);
    const itemSize = item.size || item.selectedSize || item.variant?.size || 'Free Size';
    const itemColor = item.color || item.selectedColor || item.variant?.color || '';

    return `
      <tr>
        <td style="padding: 12px 0; border-bottom: 1px solid #f0edea; vertical-align: top; width: 64px;">
          <img src="${itemImg}" alt="${item.productName || item.name}" style="width: 56px; height: 70px; object-fit: cover; border-radius: 6px; border: 1px solid #eee;" />
        </td>
        <td style="padding: 12px 14px; border-bottom: 1px solid #f0edea; vertical-align: top;">
          <div style="font-weight: 600; color: #2B2620; font-size: 14px; line-height: 1.4;">${item.productName || item.name || 'PGmart Fashion Apparel'}</div>
          <div style="color: #78716c; font-size: 12px; margin-top: 4px;">
            Size: <strong style="color: #44403c;">${itemSize}</strong>
            ${itemColor ? ` | Color: <strong style="color: #44403c;">${itemColor}</strong>` : ''}
            | Qty: <strong style="color: #44403c;">${item.quantity || 1}</strong>
          </div>
        </td>
        <td style="padding: 12px 0; border-bottom: 1px solid #f0edea; vertical-align: top; text-align: right; font-weight: 600; color: #2B2620; font-size: 14px;">
          ₹${Number(itemTotal).toLocaleString('en-IN')}
        </td>
      </tr>
    `;
  }).join('');

  const fullAddress = [
    parsedAddress.street || parsedAddress.addressLine1 || '',
    parsedAddress.addressLine2 || '',
    parsedAddress.city || '',
    parsedAddress.state ? `${parsedAddress.state} - ${parsedAddress.pincode || ''}` : (parsedAddress.pincode || ''),
  ].filter(Boolean).join(', ');

  const paymentMethodLabel = order.paymentMethod === 'cod' 
    ? 'Cash on Delivery (COD)' 
    : 'Razorpay Online (UPI / Card / NetBanking)';
  const paymentStatusBadge = order.paymentStatus === 'paid'
    ? '<span style="background-color: #ecfdf5; color: #059669; padding: 3px 8px; border-radius: 12px; font-size: 11px; font-weight: 700; text-transform: uppercase;">PAID</span>'
    : '<span style="background-color: #fffbeb; color: #d97706; padding: 3px 8px; border-radius: 12px; font-size: 11px; font-weight: 700; text-transform: uppercase;">PENDING ON DELIVERY</span>';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Order Confirmation #${orderNum}</title>
    </head>
    <body style="margin: 0; padding: 0; background-color: #f7f5f3; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
      <table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #f7f5f3; padding: 30px 10px;">
        <tr>
          <td align="center">
            <table width="100%" border="0" cellspacing="0" cellpadding="0" style="max-width: 600px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid #eae5e0;">
              <!-- Header Banner -->
              <tr>
                <td style="background: linear-gradient(135deg, #2B2620 0%, #3D352E 100%); padding: 32px 28px; text-align: center;">
                  <div style="display: inline-block; background-color: #C0654B; color: #ffffff; font-weight: 800; font-size: 20px; width: 44px; height: 44px; line-height: 44px; border-radius: 50%; margin-bottom: 12px;">PG</div>
                  <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.5px;">Order Confirmed!</h1>
                  <p style="color: #d6d3d1; margin: 8px 0 0 0; font-size: 14px;">Thank you for your order, ${customerName}.</p>
                </td>
              </tr>

              <!-- Order Summary Card -->
              <tr>
                <td style="padding: 28px 28px 12px 28px;">
                  <div style="background-color: #faf7f5; border: 1px dashed #C0654B; border-radius: 12px; padding: 18px; text-align: center; margin-bottom: 24px;">
                    <div style="color: #78716c; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; font-weight: 600;">Order Reference Number</div>
                    <div style="color: #C0654B; font-size: 22px; font-weight: 800; margin-top: 4px; letter-spacing: 0.5px;">#${orderNum}</div>
                    <div style="color: #a8a29e; font-size: 12px; margin-top: 4px;">Placed on ${formattedDate}</div>
                  </div>

                  <h3 style="color: #2B2620; font-size: 16px; margin: 0 0 12px 0; font-weight: 700; border-bottom: 2px solid #C0654B; display: inline-block; padding-bottom: 4px;">Items Ordered</h3>
                  
                  <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin-bottom: 20px;">
                    ${itemsHtml}
                  </table>

                  <!-- Totals Breakdown -->
                  <table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #faf8f6; border-radius: 10px; padding: 16px; margin-bottom: 24px;">
                    <tr>
                      <td style="padding: 4px 0; color: #78716c; font-size: 13px;">Subtotal</td>
                      <td style="padding: 4px 0; color: #2B2620; font-size: 13px; text-align: right; font-weight: 600;">₹${Number(order.subtotal || order.total).toLocaleString('en-IN')}</td>
                    </tr>
                    ${order.discount ? `
                    <tr>
                      <td style="padding: 4px 0; color: #059669; font-size: 13px;">Coupon Discount</td>
                      <td style="padding: 4px 0; color: #059669; font-size: 13px; text-align: right; font-weight: 600;">-₹${Number(order.discount).toLocaleString('en-IN')}</td>
                    </tr>` : ''}
                    <tr>
                      <td style="padding: 4px 0; color: #78716c; font-size: 13px;">Shipping Fee</td>
                      <td style="padding: 4px 0; color: #2B2620; font-size: 13px; text-align: right; font-weight: 600;">${order.shippingFee && order.shippingFee > 0 ? `₹${order.shippingFee}` : '<span style="color: #059669;">FREE</span>'}</td>
                    </tr>
                    <tr>
                      <td style="padding: 10px 0 0 0; border-top: 1px solid #e7e2dd; color: #2B2620; font-size: 16px; font-weight: 800;">Total Amount</td>
                      <td style="padding: 10px 0 0 0; border-top: 1px solid #e7e2dd; color: #C0654B; font-size: 18px; text-align: right; font-weight: 800;">₹${Number(order.total).toLocaleString('en-IN')}</td>
                    </tr>
                  </table>

                  <!-- Shipping and Payment Details -->
                  <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin-bottom: 24px;">
                    <tr>
                      <td style="vertical-align: top; width: 50%; padding-right: 12px;">
                        <div style="background-color: #ffffff; border: 1px solid #e7e2dd; border-radius: 10px; padding: 14px; height: 100%;">
                          <div style="color: #78716c; font-size: 11px; text-transform: uppercase; font-weight: 700; margin-bottom: 6px;">📍 Delivery Address</div>
                          <div style="color: #2B2620; font-weight: 600; font-size: 13px;">${customerName}</div>
                          <div style="color: #57534e; font-size: 12px; line-height: 1.4; margin-top: 4px;">${fullAddress || 'Address on file'}</div>
                          ${order.customerPhone ? `<div style="color: #78716c; font-size: 12px; margin-top: 4px;">📞 ${order.customerPhone}</div>` : ''}
                        </div>
                      </td>
                      <td style="vertical-align: top; width: 50%; padding-left: 12px;">
                        <div style="background-color: #ffffff; border: 1px solid #e7e2dd; border-radius: 10px; padding: 14px; height: 100%;">
                          <div style="color: #78716c; font-size: 11px; text-transform: uppercase; font-weight: 700; margin-bottom: 6px;">💳 Payment Details</div>
                          <div style="color: #2B2620; font-weight: 600; font-size: 13px;">${paymentMethodLabel}</div>
                          <div style="margin-top: 8px;">${paymentStatusBadge}</div>
                          <div style="color: #78716c; font-size: 11px; margin-top: 8px;">Estimated Delivery: <strong>3-5 Business Days</strong></div>
                        </div>
                      </td>
                    </tr>
                  </table>

                  <!-- Action Button -->
                  <div style="text-align: center; margin: 30px 0 10px 0;">
                    <a href="https://pgmart.in/account" style="background-color: #C0654B; color: #ffffff; padding: 14px 32px; text-decoration: none; border-radius: 30px; font-weight: 700; font-size: 14px; display: inline-block; box-shadow: 0 4px 12px rgba(192, 101, 75, 0.3);">View Order in Account</a>
                  </div>
                </td>
              </tr>

              <!-- Footer -->
              <tr>
                <td style="background-color: #faf7f5; padding: 24px; text-align: center; border-top: 1px solid #eae5e0;">
                  <p style="color: #78716c; font-size: 12px; margin: 0 0 8px 0;">
                    Need help with your order? Contact our support team at <a href="mailto:support@pgmart.in" style="color: #C0654B; text-decoration: none; font-weight: 600;">support@pgmart.in</a>
                  </p>
                  <p style="color: #a8a29e; font-size: 11px; margin: 0;">
                    © ${new Date().getFullYear()} PGmart — Trusted Fashion & Quality Store. All rights reserved.
                  </p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
}

export function generateOrderDeliveredHtml(order: any): string {
  const orderNum = order.orderNumber || order.id || 'PGM-ORDER';
  const customerName = order.customerName || 'Valued Customer';
  const deliveryDate = formatDateIST(new Date());

  let parsedItems: any[] = [];
  if (Array.isArray(order.items)) {
    parsedItems = order.items;
  } else if (typeof order.items === 'string') {
    try { parsedItems = JSON.parse(order.items); } catch {}
  }

  const itemsListHtml = parsedItems.map((item: any) => `
    <li style="padding: 6px 0; color: #44403c; font-size: 14px;">
      <strong>${item.productName || item.name || 'PGmart Apparel'}</strong> (Qty: ${item.quantity || 1})
    </li>
  `).join('');

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Your Order Has Been Delivered!</title>
    </head>
    <body style="margin: 0; padding: 0; background-color: #f7f5f3; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
      <table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #f7f5f3; padding: 30px 10px;">
        <tr>
          <td align="center">
            <table width="100%" border="0" cellspacing="0" cellpadding="0" style="max-width: 600px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid #eae5e0;">
              <!-- Header -->
              <tr>
                <td style="background: linear-gradient(135deg, #065f46 0%, #047857 100%); padding: 36px 28px; text-align: center;">
                  <div style="font-size: 40px; margin-bottom: 8px;">📦</div>
                  <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: 700;">Package Delivered!</h1>
                  <p style="color: #a7f3d0; margin: 8px 0 0 0; font-size: 14px;">Delivered on ${deliveryDate}</p>
                </td>
              </tr>

              <!-- Content -->
              <tr>
                <td style="padding: 32px 28px;">
                  <p style="color: #2B2620; font-size: 15px; line-height: 1.6; margin: 0 0 16px 0;">
                    Hi <strong>${customerName}</strong>,
                  </p>
                  <p style="color: #57534e; font-size: 14px; line-height: 1.6; margin: 0 0 20px 0;">
                    Great news! Your package for order <strong>#${orderNum}</strong> has been successfully delivered. We hope you love your new styles!
                  </p>

                  <div style="background-color: #faf7f5; border-radius: 12px; padding: 18px; margin-bottom: 24px; border: 1px solid #eae5e0;">
                    <div style="color: #78716c; font-size: 12px; text-transform: uppercase; font-weight: 700; margin-bottom: 8px;">Delivered Items:</div>
                    <ul style="margin: 0; padding-left: 20px;">
                      ${itemsListHtml || '<li style="color: #44403c;">PGmart Fashion Order</li>'}
                    </ul>
                  </div>

                  <!-- Rate & Review Banner -->
                  <div style="background: linear-gradient(135deg, #FAF7F5 0%, #F3E9E4 100%); border: 1px solid #C0654B; border-radius: 12px; padding: 22px; text-align: center; margin-bottom: 24px;">
                    <h3 style="color: #2B2620; margin: 0 0 6px 0; font-size: 16px; font-weight: 700;">How was your outfit?</h3>
                    <p style="color: #78716c; font-size: 13px; margin: 0 0 16px 0;">Help other fashion lovers by sharing your feedback and photo review!</p>
                    <a href="https://pgmart.in/account" style="background-color: #C0654B; color: #ffffff; padding: 12px 28px; text-decoration: none; border-radius: 25px; font-weight: 700; font-size: 13px; display: inline-block;">Rate & Review Products</a>
                  </div>

                  <!-- Return/Support note -->
                  <p style="color: #78716c; font-size: 12px; line-height: 1.5; text-align: center; margin: 0;">
                    Have questions or need assistance with a return / exchange? Visit our <a href="https://pgmart.in/shipping-policy" style="color: #C0654B; text-decoration: underline;">Return Policy</a> or reach out to <a href="mailto:support@pgmart.in" style="color: #C0654B; text-decoration: none; font-weight: 600;">support@pgmart.in</a>.
                  </p>
                </td>
              </tr>

              <!-- Footer -->
              <tr>
                <td style="background-color: #faf7f5; padding: 20px; text-align: center; border-top: 1px solid #eae5e0;">
                  <p style="color: #a8a29e; font-size: 11px; margin: 0;">
                    © ${new Date().getFullYear()} PGmart — Trusted Fashion & Quality Store. All rights reserved.
                  </p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
}

export function generateOrderCancellationHtml(order: any): string {
  const orderNum = order.orderNumber || order.id || 'PGM-ORDER';
  const customerName = order.customerName || 'Valued Customer';
  const cancelledByAdmin = order.cancelledBy === 'admin';
  const reason = order.cancellationReason || (cancelledByAdmin ? 'Cancelled by store administrator.' : 'Cancelled by customer request.');
  const isPrepaid = order.paymentStatus === 'paid' || order.paymentMethod !== 'cod';

  let parsedItems: any[] = [];
  if (Array.isArray(order.items)) parsedItems = order.items;
  else if (typeof order.items === 'string') { try { parsedItems = JSON.parse(order.items); } catch {} }

  const itemsListHtml = parsedItems.map((item: any) => `
    <tr>
      <td style="padding: 8px 0; border-bottom: 1px solid #fce8e6; color: #7f1d1d; font-size: 13px;">
        ${item.productName || item.name || 'PGmart Apparel'} <span style="color: #9ca3af;">(Qty: ${item.quantity || 1})</span>
      </td>
      <td style="padding: 8px 0; border-bottom: 1px solid #fce8e6; text-align: right; color: #7f1d1d; font-size: 13px; font-weight: 600;">
        ₹${Number((item.price || 0) * (item.quantity || 1)).toLocaleString('en-IN')}
      </td>
    </tr>
  `).join('');

  const now = new Date();
  const cancelDate = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' }).format(now);
  const cancelTime = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }).format(now);

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Order Cancelled: #${orderNum}</title>
    </head>
    <body style="margin: 0; padding: 0; background-color: #f7f5f3; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
      <table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #f7f5f3; padding: 30px 10px;">
        <tr><td align="center">
          <table width="100%" border="0" cellspacing="0" cellpadding="0" style="max-width: 600px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid #eae5e0;">
            <tr>
              <td style="background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%); padding: 36px 28px; text-align: center;">
                <div style="font-size: 40px; margin-bottom: 12px;">&#10060;</div>
                <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: 700;">Order Cancelled</h1>
                <p style="color: #fecaca; margin: 8px 0 0 0; font-size: 14px;">
                  ${cancelledByAdmin ? 'This order was cancelled by the store administrator.' : 'Your cancellation request has been processed.'}
                </p>
              </td>
            </tr>
            <tr><td style="padding: 28px;">
              <div style="background-color: #fef2f2; border: 1px dashed #f87171; border-radius: 12px; padding: 18px; text-align: center; margin-bottom: 24px;">
                <div style="color: #991b1b; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; font-weight: 700;">Order Reference</div>
                <div style="color: #7f1d1d; font-size: 22px; font-weight: 800; margin-top: 4px;">#${orderNum}</div>
                <div style="color: #9ca3af; font-size: 12px; margin-top: 4px;">Cancelled on ${cancelDate} at ${cancelTime} IST</div>
              </div>
              <p style="color: #2B2620; font-size: 15px; line-height: 1.6; margin: 0 0 12px 0;">Hi <strong>${customerName}</strong>,</p>
              <p style="color: #57534e; font-size: 14px; line-height: 1.6; margin: 0 0 20px 0;">
                ${cancelledByAdmin
                  ? `We regret to inform you that your order <strong>#${orderNum}</strong> has been cancelled by our store team.`
                  : `We have successfully processed your cancellation request for order <strong>#${orderNum}</strong>.`
                }
              </p>
              <div style="background-color: #fef2f2; border-left: 4px solid #ef4444; border-radius: 8px; padding: 14px 16px; margin-bottom: 24px;">
                <div style="color: #991b1b; font-size: 11px; text-transform: uppercase; font-weight: 700; margin-bottom: 4px;">Cancellation Reason</div>
                <div style="color: #7f1d1d; font-size: 14px; font-weight: 500;">${reason}</div>
              </div>
              ${itemsListHtml ? `
              <h3 style="color: #2B2620; font-size: 15px; margin: 0 0 10px 0; font-weight: 700;">Cancelled Items</h3>
              <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin-bottom: 24px;">
                ${itemsListHtml}
                <tr>
                  <td style="padding: 10px 0 0 0; border-top: 2px solid #991b1b; color: #7f1d1d; font-size: 15px; font-weight: 800;">Order Total</td>
                  <td style="padding: 10px 0 0 0; border-top: 2px solid #991b1b; text-align: right; color: #7f1d1d; font-size: 15px; font-weight: 800;">₹${Number(order.total || 0).toLocaleString('en-IN')}</td>
                </tr>
              </table>` : ''}
              ${isPrepaid ? `
              <div style="background-color: #ecfdf5; border: 1px solid #6ee7b7; border-radius: 10px; padding: 16px; margin-bottom: 24px; text-align: center;">
                <div style="font-size: 24px; margin-bottom: 6px;">&#128154;</div>
                <div style="color: #065f46; font-size: 14px; font-weight: 700; margin-bottom: 4px;">Refund Initiated</div>
                <div style="color: #047857; font-size: 13px;">Your payment of <strong>₹${Number(order.total || 0).toLocaleString('en-IN')}</strong> will be refunded to your original payment method within <strong>5&#8211;7 business days</strong>.</div>
              </div>` : `
              <div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 14px 16px; margin-bottom: 24px; text-align: center;">
                <div style="color: #166534; font-size: 13px;">Since this was a <strong>Cash on Delivery</strong> order, no payment reversal is required.</div>
              </div>`}
              <div style="text-align: center; margin: 24px 0 10px 0;">
                <a href="https://pgmart.in" style="background-color: #C0654B; color: #ffffff; padding: 13px 28px; text-decoration: none; border-radius: 30px; font-weight: 700; font-size: 14px; display: inline-block; margin-right: 8px;">Continue Shopping</a>
                <a href="https://pgmart.in/account?tab=orders" style="background-color: #f5f5f4; color: #2B2620; padding: 13px 28px; text-decoration: none; border-radius: 30px; font-weight: 700; font-size: 14px; display: inline-block;">View My Orders</a>
              </div>
            </td></tr>
            <tr><td style="background-color: #faf7f5; padding: 22px 28px; text-align: center; border-top: 1px solid #eae5e0;">
              <p style="color: #78716c; font-size: 12px; margin: 0 0 8px 0;">Questions? Contact us at <a href="mailto:support@pgmart.in" style="color: #C0654B; text-decoration: none; font-weight: 600;">support@pgmart.in</a></p>
              <p style="color: #a8a29e; font-size: 11px; margin: 0;">&copy; ${new Date().getFullYear()} PGmart &mdash; Trusted Fashion &amp; Quality Store. All rights reserved.</p>
            </td></tr>
          </table>
        </td></tr>
      </table>
    </body>
    </html>
  `;
}

/**
 * Dispatches email using Dual Strategy:
 * 1. Primary: REST API endpoint on host server (/api/emails/...)
 * 2. Secondary: Direct HTTPS Resend REST API (Guarantees delivery from browser client)
 */
export async function sendClientOrderEmail(type: 'confirmation' | 'delivered' | 'cancelled', order: any): Promise<boolean> {
  const rawEmail = order.customerEmail || order.customer_email || order.email || order.userEmail || (order.shippingAddress as any)?.email || (order.shippingAddress as any)?.customerEmail;
  const toEmail = String(rawEmail || '').trim().toLowerCase();
  if (!toEmail || !toEmail.includes('@')) {
    console.warn('[ClientMailer] Invalid recipient email address:', toEmail, order);
    return false;
  }

  const orderNum = order.orderNumber || order.id || 'PGM-ORDER';
  const isConfirm = type === 'confirmation';
  const isCancelled = type === 'cancelled';
  const subject = isConfirm
    ? `🎉 Order Confirmed: #${orderNum} - PGmart`
    : isCancelled
    ? `❌ Order Cancelled: #${orderNum} — PGmart`
    : `📦 Delivered: Your PGmart Order #${orderNum} has arrived!`;
  const html = isConfirm
    ? generateOrderConfirmationHtml(order)
    : isCancelled
    ? generateOrderCancellationHtml(order)
    : generateOrderDeliveredHtml(order);

  // 1. Primary: Hostinger Native PHP Mailer Proxy (/api/send-email.php)
  try {
    const phpRes = await fetch('/api/send-email.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, order, html })
    });
    if (phpRes.ok) {
      const text = await phpRes.text();
      if (text.startsWith('{')) {
        const data = JSON.parse(text);
        if (data && data.success) {
          console.log(`[ClientMailer] Dispatched via /api/send-email.php (${data.provider || 'Server'}) to ${toEmail}`);
          return true;
        } else {
          console.warn('[ClientMailer] /api/send-email.php returned failure:', data);
        }
      }
    }
  } catch (phpErr) {
    console.warn('[ClientMailer] /api/send-email.php failed, trying /mailer.php fallback:', phpErr);
  }

  // 2. Secondary: Root mailer.php fallback
  try {
    const rootPhpRes = await fetch('/mailer.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, order, html })
    });
    if (rootPhpRes.ok) {
      const text = await rootPhpRes.text();
      if (text.startsWith('{')) {
        const data = JSON.parse(text);
        if (data && data.success) {
          console.log(`[ClientMailer] Dispatched via /mailer.php (${data.provider || 'Server'}) to ${toEmail}`);
          return true;
        }
      }
    }
  } catch (rootPhpErr) {
    console.warn('[ClientMailer] /mailer.php failed, trying Express /api/emails:', rootPhpErr);
  }

  // 3. Tertiary: Express Node backend endpoint
  try {
    const backendEndpoint = isConfirm
      ? '/api/emails/order-confirmation'
      : isCancelled
      ? '/api/emails/order-cancelled'
      : '/api/emails/order-delivered';
    const res = await fetch(backendEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order })
    });
    if (res.ok) {
      const text = await res.text();
      if (text.startsWith('{')) {
        const data = JSON.parse(text);
        if (data && data.success) {
          console.log(`[ClientMailer] Dispatched via Express backend API to ${toEmail}`);
          return true;
        }
      }
    }
  } catch (backendErr) {
    console.warn('[ClientMailer] Express backend API unreachable:', backendErr);
  }

  // 4. Quaternary: Direct browser HTTPS Resend API dispatch
  try {
    const directResend = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${RESEND_API_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        from: SENDER_EMAIL,
        to: [toEmail],
        subject,
        html
      })
    });
    if (directResend.ok) {
      console.log(`[ClientMailer] Dispatched via direct Resend HTTPS API to ${toEmail}`);
      return true;
    }
  } catch (directErr) {
    console.warn('[ClientMailer] Direct Resend HTTPS fallback failed:', directErr);
  }

  return false;
}

